import {
    AssetAddress,
    AssetContact,
    AssetOpeningHoursPeriod,
    AssetResponse,
    AssetWeeklyOpeningResponse,
} from "../types/stores";
import {getLocale, getLocaleLang} from "./locale";

export function getReadableAddress(address: AssetAddress | undefined): string {
    if (!address) {
        return "";
    }
    const readableAddress: string[] = [];
    if (typeof address.lines !== "undefined" && address.lines.length >= 0) {
        readableAddress.push(...address.lines);
    }
    return readableAddress.join(", ");
}

export function getReadableDistance(
    distance: number | undefined,
    unitSystem = "metric"
): string {
    if (!distance) {
        return "";
    }
    let readableDistance: string;
    const meterToYard = 1.09361;
    const unitSystemValues = {
        metric: {unit: "km", smallUnit: "m", factor: 1000},
        imperial: {unit: "mi", smallUnit: "yd", factor: 1760},
    };
    const system = unitSystemValues["metric"];
    if (unitSystem === "imperial") {
        distance *= meterToYard;
    }
    if (distance < system.factor) {
        readableDistance = Math.round(distance) + "\u00A0" + system.smallUnit;
    } else {
        readableDistance =
            parseFloat((distance / system.factor).toFixed(1)) +
            "\u00A0" +
            system.unit;
    }
    return readableDistance;
}

export function getPhoneLink(contact: AssetContact | undefined): string {
    if (!contact || !contact.phone) {
        return "";
    }
    const $contactLink: HTMLAnchorElement = document.createElement("a");
    $contactLink.className = "contactPhone";
    $contactLink.href = `tel:${contact.phone}`;
    $contactLink.text = contact.phone;
    return $contactLink.outerHTML;
}

export function getWebsiteLink(contact: AssetContact | undefined): string {
    if (!contact || !contact.website) {
        return "";
    }
    const $websiteLink: HTMLAnchorElement = document.createElement("a");
    $websiteLink.className = "contactWebsite";
    $websiteLink.href = `${contact.website}`;
    $websiteLink.target = `_blank`;
    $websiteLink.text = `Go to website`;
    return $websiteLink.outerHTML;
}

export function getOpeningLabel(store: AssetResponse, locale = "en"): string {
    let openLabel = "";

    interface ILocale {
        [key: string]: Record<string, string>;
    }


    function _convertTime(UNIX_timestamp: number) {
        const a = new Date(UNIX_timestamp * 1000);
        return a.toLocaleString(getLocaleLang(), {weekday: "long", day: "numeric", month: "short"});
    }

    try {
        if (store.open?.open_now) {
            if(store.weekly_opening){
                if(checkSameTimesForAllDays(store.weekly_opening) && ! store.open?.current_slice['all-day']){
                    openLabel = `${getLocale().time.daily} ${store.open.current_slice.start} ${getLocale().time.to} ${store.open.current_slice.end} ${getLocale().time.opened}`
                }
                else {
                    openLabel = store.open.current_slice.end ? openLabel = `${getLocale().time.openUntil} ${store.open.current_slice.end} ${getLocale().time.today}` : store.open.current_slice["all-day"] ? getLocale().time.open24_7 : "";
                }
            }
            else{
                openLabel = store.open.current_slice.end ? `${getLocale().time.openUntil} ${store.open.current_slice.end} ${getLocale().time.openUntil}` : store.open.current_slice["all-day"] ? getLocale().time.open24_7 : "";
            }

        } else if (store.open?.next_opening) {
            if (isToday(new Date(store.open?.next_opening.day))) {
                openLabel +=
                    openLabel += `${getLocale().time.opensToday} ${getLocale().time.at} ${store.open.next_opening.start}`;
            } else {
                openLabel += `${getLocale().time.opens} ${_convertTime(
                    Date.parse(store.open.next_opening.day) / 1000
                )} ${getLocale().time.at} ${store.open.next_opening.start}`;
            }
        }
        else {
            openLabel = getLocale().time.temporarilyClosed;
        }
        return openLabel;
    } catch (error) {
        return "";
    }
}

export function getOpeningWeekList(weeklyOpening: AssetWeeklyOpeningResponse): string {
    interface IReadableOpeningHours {
        [key: string]: { dayName: string; hoursDay: string; today?: boolean };
    }

    const readableOpeningHours: IReadableOpeningHours = {
        "1": {dayName: getLocale().time.dayOfWeek[0], hoursDay: ""},
        "2": {dayName: getLocale().time.dayOfWeek[1], hoursDay: ""},
        "3": {dayName: getLocale().time.dayOfWeek[2], hoursDay: ""},
        "4": {dayName: getLocale().time.dayOfWeek[3], hoursDay: ""},
        "5": {dayName: getLocale().time.dayOfWeek[4], hoursDay: ""},
        "6": {dayName: getLocale().time.dayOfWeek[5], hoursDay: ""},
        "7": {dayName: getLocale().time.dayOfWeek[6], hoursDay: ""},
    };
    const $storeOpeningWeek: HTMLUListElement = document.createElement("ul");
    $storeOpeningWeek.className = "storeDetails__openingWeek";
    const today = new Date().toLocaleString(getLocaleLang(), {weekday: "long"});

    Object.entries(weeklyOpening).forEach(([day, hoursPeriod]) => {
        if (!hoursPeriod || hoursPeriod.length === 0) {
            readableOpeningHours[day].hoursDay = getLocale().time.closed;
        } else if (readableOpeningHours[day]) {
            if (readableOpeningHours[day].dayName.toLowerCase() === today.toLowerCase()) {
                readableOpeningHours[day].today = true;
            }
            if (hoursPeriod.hours?.length === 0) {
                readableOpeningHours[day].hoursDay = getLocale().time.closed;
            } else {
                readableOpeningHours[day].hoursDay = concatenateStoreHours(
                    hoursPeriod.hours
                );
            }
        }
    });
    const hoursHTML: HTMLLIElement[] = Object.entries(readableOpeningHours).map(
        ([dayIndex, dayValues]) => {
            const $listDay: HTMLLIElement = document.createElement("li");
            $listDay.className = dayValues.today ? "currentDay" : "";
            $listDay.innerHTML = `<span class='day ${dayIndex}'>${dayValues.dayName}</span><span class='hours'>${dayValues.hoursDay}</span>`;
            return $listDay;
        }
    );
    $storeOpeningWeek.append(...hoursHTML);
    return $storeOpeningWeek.outerHTML;
}

function isToday(date: Date): boolean {
    const today = new Date();
    return today.toDateString() === date.toDateString();
}

function concatenateStoreHours(openHours: AssetOpeningHoursPeriod[]) {
    const hoursText: string[] = [];
    let end = "";
    openHours.forEach((period) => {
        if (
            "all-day" in period ||
            (period.end === "00:00" && period.start === "00:00")
        ) {
            hoursText.push(`24h/24`);
            return "24h/24";
        }
        end = period.end;
        hoursText.push(`${period.start}–${end}`);
    });
    return hoursText.join(", ");
}
export function checkSameTimesForAllDays(weeklyOpening: AssetWeeklyOpeningResponse):boolean{
    if(weeklyOpening[1]?.hours?.length && weeklyOpening[1]?.hours[0]){
        const firstDayPeriod:AssetOpeningHoursPeriod = weeklyOpening[1]?.hours[0];
        const {start, end} = firstDayPeriod;
        for(let i=1; i< Object.entries(weeklyOpening).length; i++){
            const weekDayHours = Object.entries(weeklyOpening)[i][1];
            if(weekDayHours.hours?.length > 0){
                if(weekDayHours.hours[0].start !== start && weekDayHours.hours[0].end !== end){
                    return false;
                }
            }
        }
    }
    return true;
}



